import { debounce } from 'lodash';
import { useEffect, useRef, useMemo, useState } from 'react';

type UseAutoSaveProps = {
  onSave: () => void;
  dependencies: unknown[];
  delay?: number;
};

const DELAY_DEFAULT_NUMBER = 1000;

const useAutoSave = ({
  dependencies,
  onSave,
  delay = DELAY_DEFAULT_NUMBER,
}: UseAutoSaveProps) => {
  const isFirstRender = useRef(true);
  const [isSaving, setIsSaving] = useState(false);
  const onSaveRef = useRef(onSave);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  const debouncedSave = useMemo(
    () =>
      debounce(() => {
        onSaveRef.current();
        setIsSaving(false);
      }, delay),
    [delay],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setIsSaving(true);
    debouncedSave();
  }, dependencies);

  useEffect(() => {
    return () => {
      debouncedSave.flush();
    };
  }, [debouncedSave]);

  return { isSaving };
};
export default useAutoSave;
